import { Button } from '@repo/ui/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@repo/ui/components/ui/dialog';
import { Switch } from '@repo/ui/components/ui/switch';
import { cn } from '@repo/ui/lib/utils';
import { Link, useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useCookieConsentStore } from '@/stores/use-cookie-consent-store';

// Pages a visitor may need to read before choosing. The first-visit dialog stays off them so it
// can't cover the text it links to. Cookie settings in the footer still opens it on demand.
const POLICY_PATHS = new Set(['/privacy-policy', '/terms-of-service']);

export function CookieConsent() {
    const analytics = useCookieConsentStore((state) => state.analytics);
    const preferencesOpen = useCookieConsentStore((state) => state.preferencesOpen);
    const acceptAnalytics = useCookieConsentStore((state) => state.acceptAnalytics);
    const saveAnalytics = useCookieConsentStore((state) => state.saveAnalytics);
    const closePreferences = useCookieConsentStore((state) => state.closePreferences);
    const onPolicyPage = useLocation({ select: (location) => POLICY_PATHS.has(location.pathname) });
    const [analyticsOn, setAnalyticsOn] = useState(analytics === true);
    // No choice yet: the dialog has to be answered, so it can't be dismissed.
    const mustChoose = analytics === null;
    const open = preferencesOpen || (mustChoose && !onPolicyPage);

    useEffect(() => {
        if (open) setAnalyticsOn(analytics === true);
    }, [open, analytics]);

    return (
        <Dialog
            open={open}
            disablePointerDismissal={mustChoose}
            onOpenChange={(next) => {
                if (!next && !mustChoose) closePreferences();
            }}>
            <DialogContent showCloseButton={!mustChoose} className='gap-0 border-t-2 border-t-primary bg-card p-0 px-5 pt-5 pb-4 sm:max-w-xl'>
                <DialogTitle className='font-semibold text-base text-foreground leading-normal'>Cookie settings</DialogTitle>
                <DialogDescription className='mt-2 leading-snug'>
                    This website uses cookies to improve your experience. You can manage your preferences below or change them at any time from the footer.
                </DialogDescription>
                <CookieRow title='Necessary cookies' description='Required for the website to function, such as keeping you signed in.' checked disabled />
                <CookieRow
                    title='Analytics cookies'
                    description='Help us understand how visitors use the website so we can measure and improve its performance.'
                    checked={analyticsOn}
                    onCheckedChange={setAnalyticsOn}
                />
                <div className='flex flex-wrap items-center gap-2 border-border border-t pt-4'>
                    <Button type='button' className='rounded-full px-5' onClick={acceptAnalytics}>
                        Accept all
                    </Button>
                    <Button
                        type='button'
                        variant='secondary'
                        className='rounded-full bg-muted px-4 text-foreground hover:bg-muted/80'
                        onClick={() => saveAnalytics(analyticsOn)}>
                        Save preferences
                    </Button>
                    {/* New tab: this dialog covers the page, so the policy has to open somewhere it isn't covered. */}
                    <Link to='/privacy-policy' target='_blank' rel='noopener noreferrer' className='ml-auto font-medium text-sm underline underline-offset-4'>
                        Privacy Policy
                    </Link>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function CookieRow({
    title,
    description,
    checked,
    disabled,
    onCheckedChange,
}: {
    title: string;
    description: string;
    checked: boolean;
    disabled?: boolean;
    onCheckedChange?: (next: boolean) => void;
}) {
    return (
        <div className='flex items-center justify-between gap-4 border-border border-t py-3'>
            <div className='min-w-0'>
                <p className='font-semibold text-sm'>{title}</p>
                <p className='text-muted-foreground text-sm leading-snug'>{description}</p>
            </div>
            <div className='flex shrink-0 items-center gap-2'>
                <span className='text-muted-foreground text-sm'>{checked ? 'On' : 'Off'}</span>
                <Switch
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={onCheckedChange}
                    aria-label={title}
                    className={cn(disabled && 'data-checked:bg-muted')}
                />
            </div>
        </div>
    );
}
